import CallMergeIcon from '@mui/icons-material/CallMerge';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TerminalIcon from '@mui/icons-material/Terminal';
import { Box, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import { DEFAULT_BRANCH } from 'app/branches/constants';
import type { BranchPermissions } from 'app/branches/hooks/use-branch-permissions';
import { Dropdown, useDropdownClose } from 'app/common/daisy/dropdown';
import type React from 'react';
import styled from 'styled-components';
import type { SetCurrentBranchPayload } from '../../actions.js';

const DropdownContent = styled.div`
  min-width: 40rem;
  max-height: 400px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 50;
`;

const ListItem = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background-color: #f9fafb;

    .delete-action {
      opacity: 1;
    }
  }

  &:last-child {
    border-bottom: none;
  }

  .delete-action {
    opacity: 0;
    margin-left: auto;
    transition: opacity 0.2s;
  }
`;

const CurrentBranchListItem = styled(ListItem)`
  background-color: #f0f9ff;
  font-weight: 500;

  &:hover {
    background-color: #e0f2fe;
  }
`;

const ListItemText = styled.div`
  flex: 1;
  word-wrap: break-word;

  .primary {
    font-size: 14px;
    margin-bottom: 2px;
  }

  .secondary {
    font-size: 12px;
    color: #6b7280;
  }
`;

const SearchHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
`;

const tooltipBoxStyle = {
  display: 'inline-block',
  borderLeft: 1,
  borderColor: 'grey.300'
};

export type BranchSwitcherProps = {
  readonly branches: string[];
  readonly currentBranch: string;
  readonly currentBranchLoading: boolean;
  readonly handleSetCurrentBranch: (payload: SetCurrentBranchPayload) => () => void;
  readonly handleUpdateCurrentBranch: () => void;
  readonly handlePromoteCurrentBranch: () => void;
  readonly handleCreateBranch: (name: string) => () => void;
  readonly handleDeleteBranch: (name: string) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  readonly branchPermissions: BranchPermissions;
  readonly handleKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  readonly handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  readonly search: string;
  readonly unfilteredBranches: string[];
  readonly branchExists: boolean;
  readonly error: string | undefined;
  readonly currentBranchNeedsUpdate: boolean | null;
  readonly currentBranchCanPromote: boolean | null;
};

type DeleteBranchProps = {
  readonly branch: string;
  readonly branchPermissions: BranchPermissions;
  readonly handleDeleteBranch: (name: string) => (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const DeleteBranch: React.FC<DeleteBranchProps> = (props) => {
  const { branch, branchPermissions, handleDeleteBranch } = props;
  const close = useDropdownClose();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleDeleteBranch(branch)(e);
    close();
  };

  if (branch !== DEFAULT_BRANCH && branchPermissions.canDelete) {
    return (
      <IconButton
        className="delete-action"
        aria-label="delete"
        size="small"
        title="Delete branch"
        onClick={handleClick}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    );
  }

  return null;
};

const BranchSwitcherContent: React.FC<BranchSwitcherProps> = (props) => {
  const {
    currentBranch,
    currentBranchLoading,
    handleSetCurrentBranch,
    handleCreateBranch,
    handleDeleteBranch,
    branchPermissions,
    search,
    handleKeyDown,
    handleChange,
    unfilteredBranches,
    branchExists,
    error
  } = props;

  const close = useDropdownClose();

  const handleBranchClick = (branch: string) => {
    handleSetCurrentBranch({ branch })();
    close();
  };

  const handleCreateClick = () => {
    handleCreateBranch(search)();
    close();
  };

  return (
    <DropdownContent data-testid="branch-switcher-content">
      <SearchHeader>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          value={search}
          inputProps={{ maxLength: 1000 }}
          placeholder={branchPermissions.canCreate ? 'Find or create a branch…' : 'Find a branch…'}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />
      </SearchHeader>
      {currentBranch.includes(search) && (
        <CurrentBranchListItem>
          {currentBranchLoading ? (
            <CircularProgress color="primary" size="1.25rem" />
          ) : (
            <CheckIcon sx={{ color: 'success.main' }} />
          )}
          <ListItemText>
            <div className="primary">{currentBranch}</div>
            <div className="secondary">Current branch</div>
          </ListItemText>
          <DeleteBranch
            branch={currentBranch}
            branchPermissions={branchPermissions}
            handleDeleteBranch={handleDeleteBranch}
          />
        </CurrentBranchListItem>
      )}
      {unfilteredBranches.map((branch) => (
        <ListItem key={branch} onClick={() => handleBranchClick(branch)}>
          <ListItemText>
            <div className="primary">{branch}</div>
          </ListItemText>
          <DeleteBranch branch={branch} branchPermissions={branchPermissions} handleDeleteBranch={handleDeleteBranch} />
        </ListItem>
      ))}
      {search && branchPermissions.canCreate && (
        <ListItem
          onClick={!error && !branchExists ? handleCreateClick : undefined}
          style={{
            opacity: error || branchExists ? 0.5 : 1,
            cursor: error || branchExists ? 'not-allowed' : 'pointer'
          }}
          title="Create branch"
        >
          <ListItemText>
            <div className="primary">
              {branchExists ? `Cannot create "${search}"` : `Create new branch "${search}"`}
            </div>
            <div className="secondary" style={{ color: error ? '#ef4444' : '#6b7280' }}>
              {error
                ? error
                : branchExists
                  ? 'A branch with that name already exists'
                  : `Based on the "${DEFAULT_BRANCH}" branch`}
            </div>
          </ListItemText>
        </ListItem>
      )}
    </DropdownContent>
  );
};

export const BranchSwitcher: React.FC<BranchSwitcherProps> = (props) => {
  return (
    <>
      {props.branchPermissions.canList ? (
        <Dropdown
          trigger={
            <div
              data-testid="branch-switcher"
              className="text-sm max-w-48 truncate hover:bg-transparent flex gap-2 cursor-pointer border-l border-gray-300 px-2"
            >
              <TerminalIcon fontSize="small" />
              {props.currentBranch}
              <KeyboardArrowDownIcon fontSize="small" />
            </div>
          }
          position="dropdown-bottom"
          dropdownClassName="mt-2"
        >
          <BranchSwitcherContent {...props} />
        </Dropdown>
      ) : (
        <Tooltip
          enterDelay={500}
          title={
            <>
              <p>You don't have permission to change branches.</p>
              <p>Current branch: {props.currentBranch}</p>
            </>
          }
        >
          <div className="text-sm max-w-32 truncate hover:bg-transparent flex gap-2 opacity-50 cursor-not-allowed">
            <TerminalIcon fontSize="small" />
            {props.currentBranch}
            <KeyboardArrowDownIcon fontSize="small" />
          </div>
        </Tooltip>
      )}

      {props.currentBranch !== DEFAULT_BRANCH &&
        props.branchPermissions.canPromote &&
        props.currentBranchNeedsUpdate && (
          <Box sx={tooltipBoxStyle}>
            <Tooltip
              enterDelay={500}
              title={<p>Production has new changes. Click to merge them into the current branch.</p>}
            >
              <IconButton
                sx={{
                  color: 'warning.main',
                  transform: 'rotate(180deg)',
                  borderRadius: 0
                }}
                title="Update branch"
                size="small"
                onClick={props.handleUpdateCurrentBranch}
              >
                <CallMergeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      {props.currentBranch !== DEFAULT_BRANCH &&
        props.branchPermissions.canPromote &&
        props.currentBranchCanPromote && (
          <Box sx={tooltipBoxStyle}>
            <Tooltip enterDelay={500} title={<p>Click to merge the current branch into production.</p>}>
              <IconButton
                color="primary"
                title="Promote branch"
                size="small"
                onClick={props.handlePromoteCurrentBranch}
                sx={{ borderRadius: 0 }}
              >
                <CallMergeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
    </>
  );
};
